export const DASHBOARD_CONFIG = {
  recentTeamWindow: 10,
  recentModeWindow: 10,
  recentMapWindow: 5,
  recentPlayerWindow: 5,
  minimumTrendBaseline: 5,
  minimumRecentTrend: 3,
  maxFlags: 6,
  maxResults: 8,
  flags: { modeTrendPp: 10, mapTrendPp: 15, playerMetricPercent: 10 },
};

const MODES = ['HP', 'SND', 'CTL'];
const METRIC_BY_MODE = { HP: 'Impact', SND: 'ADR', CTL: 'Impact' };
const asText = (value) => String(value ?? '').trim();
const modeOf = (row) => {
  const value = asText(row.Mode || row.ModeID || row.matchId).toUpperCase();
  if (value === 'HP' || value.includes('HARDPOINT') || value.includes('MODE_HP_')) return 'HP';
  if (value === 'SND' || value.includes('SEARCH') || value.includes('MODE_SND_')) return 'SND';
  if (value === 'CTL' || value.includes('CONTROL') || value.includes('MODE_CTL_')) return 'CTL';
  return value;
};
const asNumber = (value) => {
  if (value === null || value === undefined || asText(value) === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};
const dateValue = (row) => {
  const value = asText(row.Date);
  if (!value) return null;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
};
const newestFirst = (rows) => rows.filter((row) => dateValue(row) !== null)
  .sort((a, b) => dateValue(b) - dateValue(a) || asText(b.ModeID).localeCompare(asText(a.ModeID)));
const result = (row) => {
  const value = asText(row.WinLose || row.Result).toUpperCase();
  if (value === 'WIN' || value === 'W') return 'W';
  if (value === 'LOSS' || value === 'LOSE' || value === 'L') return 'L';
  return null;
};
const record = (rows) => {
  const wins = rows.filter((row) => result(row) === 'W').length;
  const losses = rows.filter((row) => result(row) === 'L').length;
  const total = wins + losses;
  return { wins, losses, total, rate: total ? (wins / total) * 100 : null };
};
const trend = (rows, window) => {
  const dated = newestFirst(rows.filter((row) => result(row)));
  const recentEntries = dated.slice(0, window);
  const baselineEntries = dated.slice(window);
  const recent = record(recentEntries);
  const baseline = record(baselineEntries);
  const sufficient = recent.total >= DASHBOARD_CONFIG.minimumRecentTrend &&
    baseline.total >= DASHBOARD_CONFIG.minimumTrendBaseline;
  return { recent, baseline, delta: sufficient ? recent.rate - baseline.rate : null,
    recentEntries, baselineEntries };
};

export const buildDashboardAnalytics = ({ matches = [], playerRows = [], activePlayers = [] } = {}) => {
  const validMatches = matches.filter((row) => MODES.includes(modeOf(row)) && result(row));
  const datedMatches = newestFirst(validMatches);
  const teamForm = {
    record: record(validMatches),
    recent: datedMatches.slice(0, DASHBOARD_CONFIG.recentTeamWindow).map((row) => result(row)),
  };
  const modeHealth = MODES.map((mode) => {
    const rows = validMatches.filter((row) => modeOf(row) === mode);
    return { mode, record: record(rows), ...trend(rows, DASHBOARD_CONFIG.recentModeWindow) };
  });
  const mapPoolHealth = MODES.map((mode) => {
    const groups = new Map();
    validMatches.filter((row) => modeOf(row) === mode).forEach((row) => {
      const map = asText(row.Map);
      if (!map) return;
      const key = map.toUpperCase();
      if (!groups.has(key)) groups.set(key, { map, rows: [] });
      groups.get(key).rows.push(row);
    });
    return {
      mode,
      maps: [...groups.values()].map(({ map, rows }) => ({
        map, record: record(rows), ...trend(rows, DASHBOARD_CONFIG.recentMapWindow),
        historyEntries: newestFirst(rows), undatedEntries: rows.filter((row) => dateValue(row) === null),
      })).sort((a, b) => b.record.rate - a.record.rate || a.map.localeCompare(b.map)),
    };
  });

  const allowed = new Set(activePlayers.map((name) => asText(name).toUpperCase()).filter(Boolean));
  const players = new Map();
  playerRows.forEach((row) => {
    const name = asText(row.Player);
    const mode = modeOf(row);
    if (!name || !MODES.includes(mode) || (allowed.size && !allowed.has(name.toUpperCase()))) return;
    const key = name.toUpperCase();
    if (!players.has(key)) players.set(key, { name, playerId: asText(row.playerId || row.PlayerId || row.playerID) || null, modes: new Map() });
    else if (!players.get(key).playerId) players.get(key).playerId = asText(row.playerId || row.PlayerId || row.playerID) || null;
    const modes = players.get(key).modes;
    if (!modes.has(mode)) modes.set(mode, []);
    modes.get(mode).push(row);
  });
  const playerForm = [...players.values()].map(({ name, playerId, modes }) => ({
    name, playerId,
    modes: MODES.map((mode) => {
      const metric = METRIC_BY_MODE[mode];
      const rows = modes.get(mode) || [];
      const measured = newestFirst(rows.filter((row) => asNumber(row[metric]) !== null));
      const recentRows = measured.slice(0, DASHBOARD_CONFIG.recentPlayerWindow);
      const baselineRows = measured.slice(DASHBOARD_CONFIG.recentPlayerWindow);
      const average = (values) => values.length
        ? values.reduce((sum, row) => sum + asNumber(row[metric]), 0) / values.length : null;
      const current = average(measured);
      const recent = average(recentRows);
      const baseline = average(baselineRows);
      const enough = recentRows.length >= DASHBOARD_CONFIG.minimumRecentTrend &&
        baselineRows.length >= DASHBOARD_CONFIG.minimumTrendBaseline && baseline !== 0;
      return { mode, metric, current, recent, baseline,
        count: measured.length, recentCount: recentRows.length, baselineCount: baselineRows.length,
        deltaPercent: enough ? ((recent - baseline) / Math.abs(baseline)) * 100 : null,
        recentEntries: recentRows, baselineEntries: baselineRows };
    }),
  })).sort((a, b) => a.name.localeCompare(b.name));

  const coachingFlags = [];
  const addFlag = (type, title, detail, delta, threshold, evidence) => {
    if (delta === null || Math.abs(delta) < threshold) return;
    coachingFlags.push({ type, title, detail, severity: delta < 0 ? 'attention' : 'positive',
      delta, evidence });
  };
  modeHealth.forEach((item) => addFlag('modeTrend', item.mode,
    `Last ${item.recent.total}: ${item.recent.wins}-${item.recent.losses}; earlier: ${item.baseline.wins}-${item.baseline.losses}`,
    item.delta, DASHBOARD_CONFIG.flags.modeTrendPp,
    { mode: item.mode, recent: item.recent, baseline: item.baseline,
      recentEntries: item.recentEntries, baselineEntries: item.baselineEntries }));
  mapPoolHealth.forEach(({ mode, maps }) => maps.forEach((item) => addFlag('mapTrend', `${mode} / ${item.map}`,
    `Last ${item.recent.total}: ${item.recent.wins}-${item.recent.losses}; earlier: ${item.baseline.wins}-${item.baseline.losses}`,
    item.delta, DASHBOARD_CONFIG.flags.mapTrendPp,
    { mode, map: item.map, recent: item.recent, baseline: item.baseline,
      recentEntries: item.recentEntries, baselineEntries: item.baselineEntries })));
  playerForm.forEach(({ name, playerId, modes }) => modes.forEach((item) => addFlag('playerTrend', `${name} / ${item.mode} ${item.metric}`,
    `Recent ${item.recentCount} maps: ${item.recent?.toFixed(1)}; earlier ${item.baselineCount}: ${item.baseline?.toFixed(1)}`,
    item.deltaPercent, DASHBOARD_CONFIG.flags.playerMetricPercent,
    { name, playerId, mode: item.mode, metric: item.metric, recent: item.recent, baseline: item.baseline,
      recentCount: item.recentCount, baselineCount: item.baselineCount,
      recentEntries: item.recentEntries, baselineEntries: item.baselineEntries })));
  coachingFlags.sort((a, b) => (a.severity === b.severity ? Math.abs(b.delta) - Math.abs(a.delta) :
    a.severity === 'attention' ? -1 : 1));

  const recentResults = datedMatches.slice(0, DASHBOARD_CONFIG.maxResults).map((row) => ({
    id: asText(row.ModeID), date: row.Date, mode: modeOf(row), map: asText(row.Map),
    opponent: asText(row.EnemyTeam || row.VS), matchType: asText(row.MatchType),
    result: result(row), pf: asNumber(row.PF), pa: asNumber(row.PA),
  }));
  return { teamForm, modeHealth, mapPoolHealth, playerForm,
    coachingFlags: coachingFlags.slice(0, DASHBOARD_CONFIG.maxFlags), recentResults };
};
